import React, { FunctionComponent, useState } from "react";
import { IAlbum, ITrack } from "../../../hooks/albums";
import { Box, Button, Card, CardActionArea, List, Stack, Tooltip, useTheme } from "@mui/material";
import { TrackListItem } from "../../attributes/TrackListItem";
import { CustomLoadingIndicator } from "../../CustomLoadingIndicator";
import styles from "./styles.module.scss";
import { strings } from "../../../localization/LocalizedStrings";
import { TrackListDialog } from "../../attributes/TrackListDialog";
import { toast } from "react-toastify";
import { useIsMobile } from "../../../hooks/isMobile";
import { useGetVideoIds } from "../../../hooks/youTube/useGetVideoIds";
import { Icon, IconType } from "../../../icons";
import { PiperDivider } from "../../PiperDivider";

export const AlbumTrackList: FunctionComponent<IAlbumTrackListProps> = ({
  isPublic,
  className,
  album,
  artistName,
  albumName,
  fetchFromDiscogsEnabled,
  isLoading,
  onChange,
  onPlayFromYouTube,
  onFetchTrackListFromDiscogs
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const getVideoIds = useGetVideoIds();
  const [isFetchingVideoIds, setIsFetchingVideoIds] = useState(false);
  const [isTrackListDialogOpen, setIsTrackListDialogOpen] = useState(false);
  return (
    <Card
      elevation={5}
      className={className}
      sx={{ display: "flex", flexDirection: "column", alignSelf: isMobile ? "center" : "stretch" }}
      style={{ backgroundColor: theme.palette.background.default }}
    >
      <Stack direction={"row"} style={{ width: "100%", alignItems: "center" }}>
        <CardActionArea
          className={styles.trackListTitle}
          disabled={isPublic || isLoading}
          onClick={() => {
            setIsTrackListDialogOpen(true);
          }}
        >
          <p className={styles.title}>{strings.track_list_title}</p>
          <Box flexGrow={1} />
        </CardActionArea>
        {isFetchingVideoIds || isLoading ? (
          <Button className={styles.playButton} disabled>
            <CustomLoadingIndicator className={styles.loadingIndicator} />
          </Button>
        ) : (
          !isMobile &&
          album.trackList &&
          album.trackList.length > 0 && (
            <Tooltip title={strings.play_from_youtube} arrow disableInteractive>
              <Button
                className={styles.playButton}
                onClick={async () => {
                  setIsFetchingVideoIds(true);
                  const videoIds = await getVideoIds(album.trackList, albumName, artistName);
                  setIsFetchingVideoIds(false);
                  if (videoIds.length > 0) {
                    onPlayFromYouTube(videoIds);
                  } else {
                    toast.error(strings.player_unknown_error);
                  }
                }}
              >
                <Icon type={IconType.PlayCircle} />
              </Button>
            </Tooltip>
          )
        )}
      </Stack>
      {album.trackList && album.trackList.length > 0 && <PiperDivider />}
      <List className={styles.list} disablePadding>
        {!isLoading &&
          album.trackList?.map((track, index) => (
            <div key={index}>
              <TrackListItem
                key={`${album.uuid}_${index}`}
                track={track}
                artistName={artistName}
                albumName={albumName}
                onPlayFromYouTube={videoId => onPlayFromYouTube([videoId])}
              />
              {index < album.trackList.length - 1 && (
                <PiperDivider color={theme.palette.background.paper} />
              )}
            </div>
          ))}
      </List>
      {!isLoading && (
        <TrackListDialog
          key={album.uuid}
          open={isTrackListDialogOpen}
          setOpen={setIsTrackListDialogOpen}
          trackList={album.trackList}
          fetchFromDiscogsEnabled={fetchFromDiscogsEnabled}
          onChange={newTrackList => onChange(newTrackList)}
          onFetchFromDiscogs={() => onFetchTrackListFromDiscogs()}
        />
      )}
    </Card>
  );
};

export interface IAlbumTrackListProps {
  isPublic: boolean;
  className?: string;
  album: IAlbum;
  artistName: string;
  albumName: string;
  fetchFromDiscogsEnabled: boolean;
  isLoading: boolean;
  onChange: (newTrackList: ITrack[]) => void;
  onPlayFromYouTube: (videoIds: string[]) => void;
  onFetchTrackListFromDiscogs: () => void;
}
